import { useRef, useCallback } from 'react';

interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

interface UseSwipeNavigationOptions {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  threshold?: number;
  velocityThreshold?: number;
}

export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  velocityThreshold = 0.3,
}: UseSwipeNavigationOptions): SwipeHandlers {
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const isTracking = useRef(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    startTime.current = Date.now();
    isTracking.current = true;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isTracking.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - startX.current);
    const deltaY = Math.abs(touch.clientY - startY.current);

    // If vertical movement dominates, stop tracking horizontal swipe
    if (deltaY > deltaX && deltaY > 10) {
      isTracking.current = false;
    }
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!isTracking.current) return;
    isTracking.current = false;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - startX.current;
    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(deltaX) / elapsed;

    const passedThreshold = Math.abs(deltaX) > threshold;
    const passedVelocity = velocity > velocityThreshold;

    if (passedThreshold || passedVelocity) {
      if (deltaX < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    }
  }, [onSwipeLeft, onSwipeRight, threshold, velocityThreshold]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
